import React from "react";
import "./CardView.css";
import { usePreloadImages } from "../hooks/usePreloadImages";
import { fetchComments, fetchCommentCounts, postComment } from "../api/fetchComments";
import { supabase } from "../lib/supabaseClient";
import LetterAvatar from "./LetterAvatar";
import AuthPopup from "./AuthPopup";
import ProfileSetupModal from "./ProfileSetupModal";


export default function CardView({
  filteredRecords,
  formatDate,
}) {
  usePreloadImages(filteredRecords);
  
  // Local auth state management
  const [user, setUser] = React.useState(null);
  const [userProfile, setUserProfile] = React.useState(null);
  
  // Initialize auth state
  React.useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const currentUser = session?.user || null;
        setUser(currentUser);
        
        if (currentUser) {
          // Fetch user profile
          const { data } = await supabase
            .from('users')
            .select('*')
            .eq('id', currentUser.id)
            .single();
          setUserProfile(data);
        }
      } catch (error) {
        console.error('Auth init error:', error);
      }
    };
    
    initAuth();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const currentUser = session?.user || null;
      setUser(currentUser);
      
      if (currentUser) {
        const { data } = await supabase
          .from('users')
          .select('*')
          .eq('id', currentUser.id)
          .single();
        setUserProfile(data);
      } else {
        setUserProfile(null);
      }
    });
    
    return () => subscription.unsubscribe();
  }, []);
  
  // Auth functions
  const signInWithEmail = async (email) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: window.location.origin
        }
      });
      
      if (error) throw error;
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };
  
  const updateUserProfile = async (profileData) => {
    if (!user) return { success: false, error: 'No user logged in' };

    try {
      const { error } = await supabase
        .from('users')
        .upsert({
          id: user.id,
          ...profileData,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      // Refresh the profile
      const { data } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
      setUserProfile(data);
      
      return { success: true };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, error: error.message };
    }
  };
  
  // Comments state
  const [comments, setComments] = React.useState({});
  
  // Auth popup state
  const [showAuthPopup, setShowAuthPopup] = React.useState(false);
  
  // Profile setup modal state
  const [showProfileSetup, setShowProfileSetup] = React.useState(false);
  
  // Comment input states - one per recommendation
  const [commentInputs, setCommentInputs] = React.useState({});
  const [commentSubmitting, setCommentSubmitting] = React.useState({});
  
  // Load comments for all recommendations
  React.useEffect(() => {
    const loadCommentsData = async () => {
      if (filteredRecords.length === 0) return;
      
      const recommendationIds = filteredRecords
        .map(record => record.fields?.id)
        .filter(id => id != null);
      
      if (recommendationIds.length === 0) return;
      
      // Load comments for all recommendations
      const commentsData = {};
      await Promise.all(
        recommendationIds.map(async (id) => {
          const comments = await fetchComments(id);
          commentsData[id] = comments;
        })
      );
      setComments(commentsData);
    };
    
    loadCommentsData();
  }, [filteredRecords.map(r => r.fields?.id).join(',')]);
  
  // Check if user needs to complete profile setup
  React.useEffect(() => {
    if (user && userProfile !== null && !userProfile?.name) {
      setShowProfileSetup(true);
    } else if (user && userProfile?.name) {
      setShowProfileSetup(false);
    }
  }, [user, userProfile]);

  // Handle comment input click for unauthenticated users or incomplete profiles
  const handleCommentClick = () => {
    if (!user) {
      setShowAuthPopup(true);
    } else if (!userProfile?.name) {
      setShowProfileSetup(true);
    }
  };

  const handleProfileSetupComplete = async (name) => {
    setShowProfileSetup(false);
    // Refresh the user profile to get the updated data
    if (user) {
      const { data } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
      setUserProfile(data);
    }
  };

  // Handle comment input changes
  const handleCommentInputChange = (recordId, value) => {
    setCommentInputs(prev => ({
      ...prev,
      [recordId]: value
    }));
  };

  // Handle comment submission
  const handleCommentSubmit = async (recordId, recommendationId) => {
    const commentText = commentInputs[recordId]?.trim();
    if (!commentText || !user || !userProfile?.name) return;

    setCommentSubmitting(prev => ({ ...prev, [recordId]: true }));

    try {
      const result = await postComment(recommendationId, commentText, user.id);
      
      if (result.success) {
        // Clear the input
        setCommentInputs(prev => ({
          ...prev,
          [recordId]: ''
        }));
        
        // Add the new comment to the local state immediately
        const newComment = {
          id: result.data.id,
          body: commentText,
          created_at: result.data.created_at,
          user_id: user.id,
          user_name: userProfile.name,
          recommendation_id: recommendationId
        };
        
        setComments(prev => ({
          ...prev,
          [recommendationId]: [...(prev[recommendationId] || []), newComment]
        }));
      } else {
        console.error('Failed to post comment:', result.error);
      }
    } catch (error) {
      console.error('Error posting comment:', error);
    }

    setCommentSubmitting(prev => ({ ...prev, [recordId]: false }));
  };

  // Function to format date as relative time
  const formatRelativeDate = (dateString) => {
    if (!dateString) return "";
    
    let parsedDate = new Date(dateString);
    if (isNaN(parsedDate)) parsedDate = new Date(dateString.replace(/-/g, "/"));
    if (isNaN(parsedDate)) return "";
    
    const now = new Date();
    const diffInMs = now - parsedDate;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return "Today";
    if (diffInDays === 1) return "1 day ago";
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 14) return "1 week ago";
    if (diffInDays < 21) return "2 weeks ago";
    if (diffInDays < 28) return "3 weeks ago";
    if (diffInDays < 60) return "1 month ago";
    if (diffInDays < 90) return "2 months ago";
    if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      return `${months} months ago`;
    }
    const years = Math.floor(diffInDays / 365);
    return years === 1 ? "1 year ago" : `${years} years ago`;
  };

  return (
    <div className="card-grid">
      {filteredRecords.map((record) => {
        return (
          <div key={record.id} className="card">
            <div className="card-image-container">
              <img
                src={record.fields.ImageUrl || "/fallback.jpg"}
                alt={record.fields.Recommendation}
                className="card-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/fallback.jpg";
                }}
              />
            </div>
            <div className="card-content">
              <div className="card-title-row">
                <h3 className="card-title">{record.fields.Recommendation || "Untitled"}</h3>
              </div>
              <div className="card-meta-row">
                {record.fields?.Link && (
                  <a
                    href={record.fields.Link}
                    className="card-pill-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Link
                  </a>
                )}
              </div>
              <div className="card-details">
                <p className="episode-date">
                  <strong>Date:</strong> {formatDate(record.fields?.Date)}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
